import { CommonModule, DOCUMENT } from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  Inject,
  OnInit,
  signal,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterModule,
} from '@angular/router';
import { MenuDataService } from '@core/services/menu-data.service';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { AuthCustomService } from '@src/app/core/services/auth-custom/auth-custom.service';
import { CustomMenuItem } from '@src/app/core/types/menu-item.type';
import { MenuListComponent } from '@src/app/shared/components/menu-list/menu-list.component';
import { AuthActions } from '@src/app/store/auth/auth.actions';
import { AuthSelectors } from '@src/app/store/auth/auth.selectors';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ImportsModule } from 'src/app/imports';
import {
  AccountManagementDialogComponent,
  AccountMenuKey,
} from '../../dialogs/account-management.dialog/account-management.dialog.component';
import { DuplicateCoordinatedCardDialogComponent } from '../../dialogs/duplicate-coordinated-card.dialog/duplicate-coordinated-card.dialog.component';
import { UserHelpQueryParams } from '@src/app/pages/user-help/user-help-query-params.type';

@Component({
  selector: 'app-menu',
  imports: [
    CommonModule,
    RouterModule,
    ImportsModule,
    MenuListComponent,
    AccountManagementDialogComponent,
    DuplicateCoordinatedCardDialogComponent,
  ],
  templateUrl: './menu.component.html',
  styleUrl: './menu.component.scss',
})
export class MenuComponent implements AfterViewInit, OnInit {
  @ViewChild('userInfoRef', { static: false }) userInfoRef!: ElementRef;
  @Output() menuStateChange = new EventEmitter<boolean>();
  readonly #menuDataService: MenuDataService = inject(MenuDataService);
  readonly store = inject(Store);

  accountManagementDialogVisible = signal<boolean>(false);
  duplicateCardDialogVisible = signal<boolean>(false);
  initialSelectedMenu = signal<AccountMenuKey>('company-data');
  itemsWorkSpace: CustomMenuItem[];
  itemsHub: CustomMenuItem[];
  itemsUser: CustomMenuItem[];
  itemsFooter: CustomMenuItem[];

  selectedItem: string | null = '';
  visible = true;
  userMenuVisible = false; // Nuevo estado para el menú del usuario
  overLayUserTop = '500px';
  userData = this.store.selectSignal(AuthSelectors.selectAuthUser);
  loadingUserData = this.store.selectSignal(AuthSelectors.selectLoadingUser);
  selectedCompany = this.store.selectSignal(
    AuthSelectors.selectSelectedCompany,
  );
  isPoseedorFinalIntegral = this.store.selectSignal(
    AuthSelectors.selectIsPoseedorFinalIntegral,
  );
  isPoseedorFinalCE = this.store.selectSignal(
    AuthSelectors.selectIsPoseedorFinalCE,
  );
  isProductor = this.store.selectSignal(
    AuthSelectors.selectIsProductor,
  );
  queryParamsSubscription: Subscription;
  langChangeSubscription: Subscription;
  routerSubscription: Subscription;

  userFirstLetter = computed(
    () => this.userData()?.CLEAN_NOMBRE_DS?.charAt(0)?.toUpperCase() ?? '',
  );

  loadingKam = this.store.selectSignal(AuthSelectors.selectLoadingKam);
  kam = this.store.selectSignal(AuthSelectors.selectKam);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    @Inject(DOCUMENT) private document: Document,
    private translate: TranslateService,
    private authCustomService: AuthCustomService,
  ) {
    effect(() => {
      if (
        this.accountManagementDialogVisible() ||
        this.duplicateCardDialogVisible()
      ) {
        this.userMenuVisible = false;
      }
    });

    effect(() => {
      if (this.selectedCompany()) {
        this.ngOnInit();
      }
    });

    // Suscribirse a cambios de idioma
    this.langChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.initializeMenus();
    });

    // Suscribirse a cambios de navegación para expandir/contraer submenús y marcar items activos
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateMenuStateBasedOnRoute();
        this.markActiveMenuItems();
      });
  }

  ngOnInit() {
    this.checkOpenDialogs();
    this.store.dispatch(AuthActions.loadKam());
    this.initializeMenus();
    // Actualizar el estado del menú basado en la ruta actual
    this.updateMenuStateBasedOnRoute();
    // Marcar items activos en carga inicial
    this.markActiveMenuItems();
  }

  private checkOpenDialogs() {
    // Verificar si venimos de /home con queryParams específicos
    this.queryParamsSubscription = this.route.queryParams.subscribe(
      (params) => {
        if (this.router.url.includes('/home') && params['account-management']) {
          const accountManagementValue = params[
            'account-management'
          ] as AccountMenuKey;
          this.initialSelectedMenu.set(accountManagementValue);
          this.accountManagementDialogVisible.set(true);
        }
      },
    );
  }

  ngAfterViewInit() {
    // Calcular la posición inicial después de que la vista se haya inicializado
    this.calculateUserInfoPosition();
  }

  ngOnDestroy() {
    // Limpiar las suscripciones para evitar fugas de memoria
    if (this.queryParamsSubscription) {
      this.queryParamsSubscription.unsubscribe();
    }
    if (this.langChangeSubscription) {
      this.langChangeSubscription.unsubscribe();
    }
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  /**
   * Marca el item activo en todas las listas de menú basándose en la ruta actual
   * Se ejecuta una sola vez para evitar conflictos entre múltiples instancias
   */
  private markActiveMenuItems(): void {
    const currentUrl = this.router.url;
    const [path, queryString] = currentUrl.split('?');
    const currentQueryParams = this.parseQueryParams(queryString);

    // Blanquear todos los items de todas las listas
    const allLists = [this.itemsWorkSpace, this.itemsHub, this.itemsUser];

    allLists.forEach((list) => {
      if (!list) return;
      list.forEach((item) => {
        item.isActive = false;
        if (item.childs) {
          item.childs.forEach((child) => {
            child.isActive = false;
          });
        }
      });
    });

    // Buscar y marcar el item que coincide con la ruta actual
    for (const list of allLists) {
      if (!list) continue;

      for (const item of list) {
        if (item.childs) {
          for (const child of item.childs) {
            if (
              this.isRouteMatch(
                child.route,
                child.queryParams,
                path,
                currentQueryParams,
              )
            ) {
              child.isActive = true;
              item.isActive = true;
              return;
            }
          }
        } else {
          if (
            this.isRouteMatch(
              item.route,
              item.queryParams,
              path,
              currentQueryParams,
            )
          ) {
            item.isActive = true;
            return;
          }
        }
      }
    }
  }

  /**
   * Actualiza el estado del menú (expandido/contraído) basándose en la ruta actual
   */
  private updateMenuStateBasedOnRoute(): void {
    // Validar que itemsWorkSpace esté inicializado
    if (!this.itemsWorkSpace || this.itemsWorkSpace.length === 0) {
      return;
    }

    const currentUrl = this.router.url;
    const [path, queryString] = currentUrl.split('?');
    const currentQueryParams = this.parseQueryParams(queryString);

    // Recorrer los items del workspace para encontrar el que coincida con la ruta actual
    this.itemsWorkSpace.forEach((item) => {
      if (item.childs) {
        // Verificar si algún hijo coincide con la ruta actual
        const hasActiveChild = item.childs.some((child) =>
          this.isRouteMatch(
            child.route,
            child.queryParams,
            path,
            currentQueryParams,
          ),
        );

        // Expandir el menú padre si tiene un hijo activo
        item.isChildVisible = hasActiveChild;
      }
    });
  }

  /**
   * Parsea los query params de una URL
   */
  private parseQueryParams(
    queryString: string | undefined,
  ): Record<string, string> {
    if (!queryString) return {};

    const params: Record<string, string> = {};
    queryString.split('&').forEach((param) => {
      const [key, value] = param.split('=');
      if (key) params[key] = value || '';
    });
    return params;
  }

  /**
   * Verifica si una ruta coincide con la ruta actual
   */
  private isRouteMatch(
    route: string | string[] | null | undefined,
    queryParams: Record<string, any> | null | undefined,
    currentPath: string,
    currentQueryParams: Record<string, string>,
  ): boolean {
    if (!route) return false;

    const routePath = Array.isArray(route) ? route[0] : route;
    const pathMatches = currentPath === routePath;

    if (!pathMatches) return false;
    if (!queryParams) return true;

    // Verificar que todos los queryParams del menú coincidan
    return Object.keys(queryParams).every(
      (key) => currentQueryParams[key] === queryParams[key],
    );
  }

  /**
   * Inicializa los menús con las traducciones actuales
   */
  private initializeMenus(): void {
    this.itemsWorkSpace = this.#menuDataService.getMenuListWorkSpace(
      this.accountManagementDialogVisible,
      this.initialSelectedMenu,
      this.isPoseedorFinalIntegral(),
      this.isPoseedorFinalCE(),
      this.isProductor(),
    );
    this.itemsHub = this.#menuDataService.getMenuListHub(
      this.isPoseedorFinalIntegral(),
      this.isPoseedorFinalCE(),
      this.isProductor(),
    );
    this.itemsUser = this.#menuDataService.getMenuListUser(
      this.accountManagementDialogVisible,
      this.initialSelectedMenu,
      this.duplicateCardDialogVisible,
    );
    this.itemsFooter = this.#menuDataService.getMenuListFooter();
  }

  /**
   * Calcula la posición del botón user-info y actualiza overLayUserTop
   */
  calculateUserInfoPosition(): void {
    if (this.userInfoRef?.nativeElement) {
      const userInfoElement = this.userInfoRef.nativeElement;
      const rect = userInfoElement.getBoundingClientRect();

      const topPosition = rect.top - 198;
      const minTop = 20;
      const panelHeight = 455; // altura estimada del panel
      const maxTop = window.innerHeight - panelHeight - 20;
      // Limitar para que no se salga por arriba ni por abajo
      const finalPosition = Math.max(minTop, Math.min(topPosition, maxTop));
      this.overLayUserTop = `${finalPosition}px`;
    }
  }

  /**
   * Recalcula la posición cuando se abre el menú de usuario
   */
  toggleUserMenu(): void {
    if (!this.userMenuVisible) {
      // Recalcular posición antes de abrir
      this.calculateUserInfoPosition();
    }
    this.userMenuVisible = !this.userMenuVisible;
  }

  toggleMenu(): void {
    this.visible = !this.visible;
    this.menuStateChange.emit(this.visible);
  }

  toggleSubmenu(item: CustomMenuItem): void {
    if (item.childs && item.childs.length > 0) {
      item.isChildVisible = !item.isChildVisible;
      if (!this.visible) {
        this.visible = !this.visible;
        this.menuStateChange.emit(this.visible);
      }
      // Cerrar otros submenús cuando se abre uno nuevo
      this.itemsWorkSpace.forEach((menuItem) => {
        if (menuItem !== item && menuItem.childs) {
          menuItem.isChildVisible = false;
        }
      });
    }
  }

  navigate(item: CustomMenuItem): void {
    this.toggleSubmenu(item);
    this.visible = true;

    // Si tiene ruta, se navegará automáticamente por routerLink
  }

  onClickContact() {
    this.router.navigate(['/help'], {
      queryParams: { source: 'menu' } as UserHelpQueryParams,
    });
  }

  onClickContacts() {
    this.router.navigate(['account-management/contacts']);
  }

  closeUserMenu(): void {
    this.userMenuVisible = false;
  }

  logout(): void {
    this.authCustomService.logout();
  }
}
